/* eslint-disable max-lines-per-function */
import { GetServerSideProps } from 'next'
import isEmpty from 'lodash/isEmpty'
import {
  getIndianStocksFnoCatalogue,
  getIndianStocksRelatedBlogsUrl,
  IN_STOCKS_CATELOGUE,
  indianStocksBackgroundApiBaseUrl,
  strapiINstocksApi,
} from '@/root/api/apiConstants'
import { getCustomDeviceDetails } from '@/root/utils/common/web/get-mobile-os'
import { TAssetDetailsData } from '@/components/Stocks/Widgets/types'
import INStockDetail from '@/components/Stocks/Pages/INStockDetailPage'
import {
  INDSTOCKS_HOST,
  triadRedirectionBaseUrl,
} from '@/root/constants/common'
import pageShortCircuit from '@/root/utils/network-adapter/short-circuit'
import { addQueryParamsToApiV2 } from '@/root/helpers'
import { HTTP_STATUS_CODE } from '@/root/constants/api-status-codes'
import { TStrapiV4PageType } from '@/stories/strapi-4/common-types'
import { setCfCacheHeaders } from '@/root/utils/cf-cache/setCacheHeaders'
import { ICategoryFnO } from '@/components/Stocks/FnO/types/options-chain'
import { TRelatedBlogs } from '@/root/utils/articles/types'

const StocksL2 = props => {
  return <INStockDetail {...props} />
}

export default StocksL2

// eslint-disable-next-line complexity
export const getServerSideProps: GetServerSideProps = async context => {
  // if request has already been sent back do nothing, this is when
  // res.end might be called _app.tsx MyApp.getInitialProps
  if (context.res.headersSent) {
    return {
      props: {},
    }
  }

  const slug = context.params
  const scrip_asset_type =
    (context.query.scrip_asset_type as string | null) ?? null
  const token = context.query.token ?? context.req.cookies.token ?? null

  // Redirect to terminal if token is present
  if (token) {
    return {
      redirect: {
        destination: `${triadRedirectionBaseUrl}/stocks/explore/${slug?.stockSlug}`,
        permanent: false,
      },
    }
  }

  const headers = {
    Platform: 'web',
  }

  const deviceDetails = getCustomDeviceDetails(
    context.req.headers['user-agent'],
  )

  const isMobile =
    deviceDetails.platform === 'unknown' ? null : deviceDetails.isMobile

  let errorStatusCode: number | undefined

  let redirectUrl = ''

  const url = addQueryParamsToApiV2(
    `${indianStocksBackgroundApiBaseUrl}${IN_STOCKS_CATELOGUE}${slug?.stockSlug}`,
    {
      response_format: 'web',
      'catalog-required': 'true',
      'graph-optional': 'true',
      ...(scrip_asset_type && { 'scrip-asset-type': scrip_asset_type }),
    },
  )

  const strapiApiUrl = strapiINstocksApi(slug?.stockSlug as string)
  const fnoApiUrl = getIndianStocksFnoCatalogue(slug?.stockSlug as string)
  const inStocksRelatedBlogsUrl = getIndianStocksRelatedBlogsUrl(
    slug?.stockSlug as string,
  )

  const {
    successfulApiRequests,
    failedApiRequestsBeforeTimeout,
    timedoutApiRequests,
  } = await pageShortCircuit(
    [
      {
        url,
        headers,
      },
      {
        url: strapiApiUrl,
      },
      {
        url: fnoApiUrl,
      },
      {
        url: inStocksRelatedBlogsUrl,
      },
    ],
    1500,
  )

  const data = successfulApiRequests.find(data => data.url === url)
    ?.value as TAssetDetailsData | null

  const strapiApiStockData = successfulApiRequests.find(
    data => data.url === strapiApiUrl,
  )?.value as TStrapiV4PageType | null

  const apiFailData = failedApiRequestsBeforeTimeout.find(
    data => data.url === url,
  )

  const fnoApiData = successfulApiRequests.find(data => data.url === fnoApiUrl)
    ?.value as ICategoryFnO | null

  const inStocksRelatedBlogs = successfulApiRequests.find(
    data => data.url === inStocksRelatedBlogsUrl,
  )?.value?.data as TRelatedBlogs | null

  if (apiFailData) {
    errorStatusCode = apiFailData.statusCode
      ? apiFailData.statusCode
      : HTTP_STATUS_CODE.NOT_FOUND
  }

  if (timedoutApiRequests.some(req => req.url === url)) {
    errorStatusCode = HTTP_STATUS_CODE.SERVICE_UNAVAILABLE
  }

  const { relativepath = '' } = data?.catalog?.entity_basic ?? {}
  const baseUrlObj = context.resolvedUrl.split('?')[0]

  if (relativepath !== baseUrlObj) {
    redirectUrl = relativepath
  }

  const isMarketOpen = data?.market_status?.current_market_status !== 'close'

  // Not to cache the response if the market is open
  if (context.req.headers.host === INDSTOCKS_HOST) {
    context.res.setHeader('X-Middleware-Caching', 'false')
  }

  if (errorStatusCode) {
    context.res.statusCode = errorStatusCode
    context.res.setHeader('X-Middleware-Caching', 'false')
  }

  if (!isEmpty(data?.catalog)) {
    if (isMarketOpen) {
      setCfCacheHeaders(context.res, '/stocks/[stockSlug]/market-open')
    } else {
      setCfCacheHeaders(context.res, '/stocks/[stockSlug]')
    }
  }

  if (redirectUrl) {
    return {
      redirect: {
        destination: redirectUrl,
        statusCode: 301,
      },
    }
  }

  return {
    props: {
      data: data ?? {},
      strapiApiResponse: strapiApiStockData ?? null,
      fnoApiData: fnoApiData ?? null,
      inStocksRelatedBlogs: inStocksRelatedBlogs ?? null,
      host: context.req.headers.host ?? '',
      errorStatusCode: errorStatusCode ?? HTTP_STATUS_CODE.SUCCESS,
      isMobile,
      headers,
    },
  }
}
